import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import { API, BRAND } from '../config';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import SignupTermsCheckbox from '../components/consent/SignupTermsCheckbox';
import { MetaConsentReminder, metaReminderSeen, markMetaReminderSeen } from '../components/MetaConsentReminder';
import { getFbAttributionPayload, trackCompleteRegistration, setMetaConsent, ensurePixelInit } from '../lib/metaPixel';
import { getSignupAttribution } from '../lib/utmCapture';
import { getStoredInviteCode, inviteCodeSource } from '../lib/inviteCode';
import { getPushTokenIfGranted as requestNotificationPermission, isNotificationSupported, getPushPlatform } from '../services/pushService';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const processedRef = React.useRef(false);
  const [requiresConsent, setRequiresConsent] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [metaConsent, setMetaConsentState] = useState(false);
  const [referralCode, setReferralCode] = useState(getStoredInviteCode);
  const [metaReminder, setMetaReminder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const registerPush = async (accessToken) => {
    try {
      const fcmToken = await requestNotificationPermission();
      if (fcmToken) {
        await axios.post(`${API}/notifications/register-token`, {
          token: fcmToken,
          platform: getPushPlatform()
        }, { headers: { Authorization: `Bearer ${accessToken}` } });
        console.log('Push token registered for Google user');
      }
    } catch (e) {
      console.warn('Push registration failed:', e.message);
    }
  };

  const handleExistingUser = async (responseData) => {
    const userData = responseData.user || responseData;
    if (responseData.access_token) localStorage.setItem('auth_token', responseData.access_token);
    setUser(userData);
    await registerPush(responseData.access_token);
    navigate("/dashboard", { replace: true });
  };

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processAuth = async () => {
      const params = new URLSearchParams(location.search);
      const googleCode = params.get("code");
      const consentFlow = params.get("consent");
      const serverToken = params.get("server_token");

      // Server-side callback completed: token received via URL
      if (serverToken) {
        localStorage.setItem('auth_token', serverToken);
        localStorage.removeItem('logged_out');
        axios.defaults.headers.common['Authorization'] = `Bearer ${serverToken}`;

        // Broadcast token to TWA via BroadcastChannel
        try {
          const channel = new BroadcastChannel('rvincente-auth');
          channel.postMessage({ type: 'AUTH_SUCCESS', token: serverToken });
          setTimeout(() => channel.close(), 1000);
        } catch (e) { console.warn('BroadcastChannel not available'); }

        // Fetch user data and navigate
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            setUser(response.data);
            await registerPush(serverToken);
          }
        } catch (e) { console.warn('Failed to fetch user after server callback'); }

        // iter239 — Flag per aprire il popup del codice amico in Dashboard
        // al primo login Google (saltabile). Solo se l'utente non l'ha
        // ancora applicato.
        try {
          if (localStorage.getItem('referralCodeApplied') !== '1') {
            localStorage.setItem('pendingReferralPopup', '1');
          }
        } catch {}

        navigate("/dashboard", { replace: true });
        return;
      }

      // Server-side consent redirect (new user from server-callback)
      if (consentFlow === "true") {
        setRequiresConsent(true);
        setPendingSession({
          pending_token: params.get("pending_token"),
          email: params.get("email"),
          name: params.get("name"),
          picture: params.get("picture")
        });
        return;
      }

      // Google auth code → redirect to server-side handler
      if (googleCode) {
        const intentId = params.get("state") || "";
        const redirectUri = window.location.origin + '/auth/google/callback';
        window.location.href = `/api/auth/google/server-callback?code=${encodeURIComponent(googleCode)}&redirect_uri=${encodeURIComponent(redirectUri)}&intent_id=${encodeURIComponent(intentId)}`;
        return;
      }

      // LEGACY: Emergent auth flow (session_id in hash)
      const hash = location.hash;
      const sessionId = hash?.split("session_id=")[1];
      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          if (response.data.requires_consent) {
            setRequiresConsent(true);
            setPendingSession({ session_id: sessionId, email: response.data.email, name: response.data.name, picture: response.data.picture });
          } else {
            await handleExistingUser(response.data);
          }
        } catch (err) { navigate("/login", { replace: true }); }
        return;
      }

      navigate("/login", { replace: true });
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, setUser]);

  const handleConsentSubmit = async (metaOverride) => {
    if (!acceptsTerms) { setError("Devi confermare di avere almeno 18 anni e accettare i Termini di servizio"); return; }
    const metaOk = typeof metaOverride === 'boolean' ? metaOverride : metaConsent;
    if (typeof metaOverride !== 'boolean' && !metaOk && !metaReminderSeen()) {
      markMetaReminderSeen();
      setMetaReminder({
        accept: () => { setMetaReminder(null); setMetaConsentState(true); ensurePixelInit(); handleConsentSubmit(true); },
        decline: () => { setMetaReminder(null); handleConsentSubmit(false); },
      });
      return;
    }
    setLoading(true); setError("");
    try {
      const consentData = {
        is_over_18: true, accepts_notifications: true, marketing_consent: false,
        meta_tracking_consent: metaOk, accepts_terms: true,
        referral_code: (referralCode || "").trim().toUpperCase() || undefined,
        referral_code_source: inviteCodeSource(referralCode),
        ...getSignupAttribution(),
      };
      let response;
      if (pendingSession.pending_token) {
        response = await axios.post(`${API}/auth/google/exchange`, {
          pending_token: pendingSession.pending_token,
          consent_data: consentData,
          ...getFbAttributionPayload(),
          ...getSignupAttribution(),
        });
      } else {
        response = await axios.post(`${API}/auth/session`, { session_id: pendingSession.session_id, consent_data: consentData, ...getFbAttributionPayload() });
      }

      if (response.data.requires_consent) {
        setError("Devi confermare di avere almeno 18 anni e accettare i Termini di servizio");
      } else {
        const userData = response.data.user || response.data;
        if (response.data.access_token) localStorage.setItem('auth_token', response.data.access_token);
        localStorage.removeItem('logged_out');
        setUser(userData);
        if (response.data?.status === 'new') setMetaConsent(metaOk);
        if (response.data?.status === 'new' && userData?.user_id && metaOk) trackCompleteRegistration(userData.user_id);

        if (response.data.access_token) {
          try {
            const channel = new BroadcastChannel('rvincente-auth');
            channel.postMessage({ type: 'AUTH_SUCCESS', token: response.data.access_token });
            setTimeout(() => channel.close(), 1000);
          } catch (e) {}
          await registerPush(response.data.access_token);
        }
        navigate("/dashboard", { replace: true });
      }
    } catch (err) { setError("Errore durante la registrazione. Riprova."); }
    finally { setLoading(false); }
  };

  if (requiresConsent && pendingSession) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex flex-col items-center justify-center p-4" data-testid="consent-page">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src={BRAND.logo} alt={BRAND.name} className="w-20 h-20 mx-auto mb-4 rounded-2xl object-contain shadow-[0_0_30px_rgba(245,166,35,0.2)]" />
            <h1 className="text-3xl font-black uppercase text-white mb-2" style={{fontFamily: 'Barlow Condensed'}}>Completa Registrazione</h1>
            <p className="text-gray-500 text-sm">Ciao {pendingSession.name}!</p>
          </div>
          <div className="glass rounded-3xl p-6">
            {pendingSession.picture && <div className="flex justify-center mb-4"><img src={pendingSession.picture} alt="" className="w-16 h-16 rounded-full" /></div>}
            <p className="text-center text-gray-400 text-sm mb-6">Per completare la registrazione con Google, conferma:</p>
            {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="consent-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
            <div className="space-y-4 mb-6">
              <SignupTermsCheckbox checked={acceptsTerms} onChange={setAcceptsTerms} testId="google-callback-terms" />
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="google-callback-meta-label">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${metaConsent ? 'bg-[#F5A623] border-[#F5A623]' : 'border-gray-600 group-hover:border-gray-500'}`}>{metaConsent && <Check className="w-3 h-3 text-[#1A1A1A]" />}</div>
                <input type="checkbox" checked={metaConsent} onChange={(e) => { setMetaConsentState(e.target.checked); if (e.target.checked) ensurePixelInit(); }} className="hidden" data-testid="google-callback-meta" />
                <span className="text-sm text-gray-400">Aiutaci a far conoscere RVincente: consenti a Meta di misurare le nostre pubblicità. <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#F5A623] underline">Info</a></span>
              </label>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Codice invito o influencer <span className="text-gray-600">(facoltativo)</span></label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                  placeholder="Es. KR45 o MARIO"
                  maxLength={8}
                  autoCapitalize="characters"
                  className="w-full h-11 px-4 bg-black/30 border border-gray-700 rounded-xl text-white uppercase tracking-widest font-mono text-center placeholder:text-gray-600 placeholder:normal-case placeholder:tracking-normal placeholder:font-sans focus:border-[#F5A623]"
                  data-testid="google-callback-referral-code"
                />
                <p className="text-[10px] text-gray-500 mt-1">Se un amico ti ha invitato, inserisci qui il suo codice</p>
              </div>
            </div>
            <button onClick={() => handleConsentSubmit()} disabled={loading} className="w-full h-14 btn-primary rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="consent-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Completa Registrazione"}
            </button>
          </div>
        </div>
        <MetaConsentReminder open={!!metaReminder} onAccept={() => metaReminder?.accept()} onDecline={() => metaReminder?.decline()} onClose={() => setMetaReminder(null)} />
      </div>
    );
  }

  return <LoadingScreen />;
};

export default AuthCallback;
